import React, { useEffect, useRef, useState } from 'react';
import { getVmsCamaras } from '../services/api';

// Carga (una vez) el componente <video-stream> de go2rtc, servido por el proxy
// /go2rtc. Importa video-rtc.js relativo -> tambien pasa por el proxy (cookie vms).
let _loading = false;
function loadGo2rtc(base) {
  if (_loading || (window.customElements && customElements.get('video-stream'))) return;
  _loading = true;
  const s = document.createElement('script');
  s.type = 'module';
  s.src = (base || '') + '/go2rtc/video-stream.js';
  s.onerror = () => { _loading = false; };
  document.head.appendChild(s);
}

// Reproductor en vivo via MSE (WebSocket) -> tiempo real, sin congelarse. El
// media viaja por el WS (/go2rtc/api/ws), no requiere UDP/WebRTC. Auth por
// ?token= en la URL (la cookie no autoriza el handshake del WS; el token si).
// `base` permite servir el video desde un subdominio DNS-only (fuera de
// Cloudflare, que no pasa bien el WebSocket de video). Vacio = mismo origen.
function VideoPlayer({ base, name, mode = 'mse' }) {
  const ref = useRef(null);
  const token = localStorage.getItem('token') || '';
  const src = (base || '') + '/go2rtc/api/ws?src=' + encodeURIComponent(name) + '&token=' + encodeURIComponent(token);
  useEffect(() => {
    let alive = true;
    if (!window.customElements) return undefined;
    customElements.whenDefined('video-stream').then(() => {
      if (alive && ref.current) {
        try { ref.current.mode = mode; ref.current.src = src; } catch (e) { /* noop */ }
      }
    });
    return () => { alive = false; };   // al desmontar, el componente cierra su WS solo
  }, [src, mode]);
  return React.createElement('video-stream', { ref });
}

const VmsPared = () => {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [suc, setSuc] = useState(null);
  const [cols, setCols] = useState(4);
  const [expanded, setExpanded] = useState(null);   // {src, label}

  useEffect(() => {
    // cookie para que el proxy /go2rtc autorice el WS/script/video (mismo-origen)
    const token = localStorage.getItem('token');
    if (token) {
      const secure = window.location.protocol === 'https:' ? '; Secure' : '';
      document.cookie = `vms=${token}; path=/go2rtc; SameSite=Lax${secure}`;
    }
    getVmsCamaras()
      .then((r) => {
        setData(r.data);
        loadGo2rtc(r.data.video_base);   // carga el player desde el origen del video
        if (r.data.sucursales && r.data.sucursales.length) setSuc(r.data.sucursales[0].sucursal);
      })
      .catch((e) => setErr(e.response?.data?.detail || e.message));
  }, []);

  if (err) return <div style={S.msg}>Error cargando cámaras: {String(err)}</div>;
  if (!data) return <div style={S.msg}>Cargando cámaras…</div>;

  const grupo = data.sucursales.find((g) => g.sucursal === suc);
  const cams = grupo ? grupo.camaras : [];

  return (
    <div style={S.wrap}>
      <style>{CSS}</style>
      <div style={S.toolbar}>
        <div>
          <h2 style={S.title}>Cámaras de sucursales</h2>
          <div style={S.sub}>{data.total} cámaras · {data.sucursales.length} sucursales</div>
        </div>
        <div style={S.gridCtl}>
          <span style={{ color: '#6b7280', fontSize: 13 }}>Rejilla</span>
          {[2, 3, 4, 5, 6].map((n) => (
            <button key={n} onClick={() => setCols(n)}
              style={n === cols ? S.colBtnA : S.colBtn}>{n}×{n}</button>
          ))}
        </div>
      </div>

      <div style={S.sucBar}>
        {data.sucursales.map((g) => (
          <button key={String(g.sucursal)} onClick={() => setSuc(g.sucursal)}
            style={g.sucursal === suc ? S.sucBtnA : S.sucBtn}>
            Suc {g.sucursal ?? '—'} <span style={S.badge}>{g.camaras.length}</span>
          </button>
        ))}
      </div>

      <div style={{ ...S.grid, gridTemplateColumns: `repeat(${cols}, 1fr)` }}>
        {cams.map((c) => {
          const label = `${c.nombre || 'Grabador'} · C${c.canal}`;
          return (
            <div key={c.stream} style={S.cell} title={label}
              onClick={() => setExpanded({ name: c.stream, label })}>
              <VideoPlayer base={data.video_base} name={c.stream} />
              <span style={S.label}>{label}</span>
            </div>
          );
        })}
        {cams.length === 0 && <div style={S.msg}>Sin cámaras en esta sucursal.</div>}
      </div>

      {expanded && (
        <div style={S.modal} onClick={() => setExpanded(null)}>
          <div style={S.modalInner} onClick={(e) => e.stopPropagation()}>
            <div style={S.modalHead}>
              <span>{expanded.label}</span>
              <button style={S.closeBtn} onClick={() => setExpanded(null)}>✕</button>
            </div>
            <div style={S.modalVideo}>
              <VideoPlayer base={data.video_base} name={expanded.name} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const CSS = `
  video-stream { display:block; width:100%; height:100%; background:#000; }
  video-stream video { width:100%; height:100%; object-fit:contain; background:#000; display:block; }
`;

const S = {
  wrap: { padding: 24 },
  msg: { padding: 40, color: '#6b7280', fontSize: 15 },
  toolbar: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16, flexWrap: 'wrap', gap: 12 },
  title: { margin: 0, fontSize: 22, color: '#111827' },
  sub: { color: '#6b7280', fontSize: 13, marginTop: 4 },
  gridCtl: { display: 'flex', alignItems: 'center', gap: 6 },
  colBtn: { padding: '6px 10px', border: '1px solid #d1d5db', background: '#fff', color: '#374151', borderRadius: 6, cursor: 'pointer', fontSize: 13 },
  colBtnA: { padding: '6px 10px', border: '1px solid #3b82f6', background: '#3b82f6', color: '#fff', borderRadius: 6, cursor: 'pointer', fontSize: 13 },
  sucBar: { display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 },
  sucBtn: { padding: '6px 12px', border: '1px solid #d1d5db', background: '#fff', color: '#374151', borderRadius: 20, cursor: 'pointer', fontSize: 13 },
  sucBtnA: { padding: '6px 12px', border: '1px solid #1f2937', background: '#1f2937', color: '#fff', borderRadius: 20, cursor: 'pointer', fontSize: 13 },
  badge: { background: 'rgba(0,0,0,.15)', borderRadius: 10, padding: '0 6px', marginLeft: 4, fontSize: 11 },
  grid: { display: 'grid', gap: 6 },
  cell: { position: 'relative', aspectRatio: '16 / 9', background: '#000', borderRadius: 6, overflow: 'hidden', cursor: 'pointer' },
  label: { position: 'absolute', left: 6, bottom: 5, color: '#e5e7eb', fontSize: 11, background: 'rgba(0,0,0,.55)', padding: '1px 6px', borderRadius: 4, pointerEvents: 'none' },
  modal: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modalInner: { width: '82vw', maxWidth: 1280, background: '#111', borderRadius: 10, overflow: 'hidden' },
  modalHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', color: '#e5e7eb', fontSize: 14 },
  modalVideo: { aspectRatio: '16 / 9', background: '#000' },
  closeBtn: { background: 'transparent', border: 'none', color: '#e5e7eb', fontSize: 18, cursor: 'pointer' },
};

export default VmsPared;
